/**
 * Inline stylesheet for the Card Match round scene. Split out so the round
 * module stays focused on view logic. Mounted once per page via
 * `ensureRoundStyles()`.
 *
 * Layout (1920x1080):
 *   - Three-row grid: header (auto) / board (1fr) / footer (auto).
 *   - Right gutter reserved at 460px so the side-leaderboard panel
 *     (left=1500..1880, width=380) never overlaps the board. Mirrors the
 *     anagram / who-am-i / horse-race scene layout for visual parity.
 *
 * Card back design (Bug 1 fix): the number IS the back. No brand-icon SVG
 * underlay — just a brand-coloured gradient with a huge centered Bebas Neue
 * number. Reads cleanly from a stream at the smallest grid size (6x8 = 48
 * cards on 1920x1080, ~150px per card after the right gutter is reserved).
 */

const STYLE_ID = 'cm-round-style';

export function ensureRoundStyles(rootId: string): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    #${rootId} {
      position: absolute;
      inset: 0;
      display: block;
      z-index: 30;
    }
    #${rootId} .cm-round-grid {
      /* Fixed pixel bands (header 120px, footer 76px) instead of auto/1fr.
         Why: a sibling reflow (e.g. web-font Oswald arriving late and
         pushing the header line-height by ~6px) used to nudge the 1fr
         board cell, recomputing --cm-tile-size and visibly resizing every
         tile mid-round. Fixed-pixel rows match the stage geometry that
         round.ts uses to compute --cm-tile-size, so the board area is
         deterministic regardless of font/leaderboard/splash state. */
      display: grid;
      grid-template-rows: 120px 1fr 76px;
      gap: 0;
      /* Right padding reserves room for the side leaderboard panel
         (left=1500..1880). Left padding matches anagram (80px). */
      padding: 36px 460px 24px 80px;
      height: 100%;
      box-sizing: border-box;
    }
    #${rootId} .cm-round-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 24px;
      /* Fixed band height — font-swap / line-height changes can't push the
         board down anymore. overflow:hidden contains any text the fallback
         font happens to render taller than the band. */
      height: 120px;
      box-sizing: border-box;
      overflow: hidden;
    }
    #${rootId} .cm-round-tag, #${rootId} .cm-round-pairs {
      font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
      font-size: 40px;
      font-weight: 700;
      letter-spacing: 0.18em;
      /* White header text for max contrast against the dark animated bg
         (was sg-secondary cyan, which read fine but the user wanted white).
         Brand colour preserved as a soft glow. */
      color: #FFFFFF;
      text-shadow: 0 0 20px rgba(0, 229, 255, 0.35), 0 2px 4px rgba(0, 0, 0, 0.55);
      flex: 1;
      text-transform: uppercase;
    }
    #${rootId} .cm-round-pairs { text-align: right; }
    #${rootId} .cm-round-timer {
      font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
      font-size: 88px;
      font-weight: 700;
      letter-spacing: 0.10em;
      color: #fff;
      font-variant-numeric: tabular-nums;
      transition: color 0.2s;
      text-shadow: 0 0 24px rgba(255, 255, 255, 0.25);
    }
    #${rootId} .cm-round-timer-urgent {
      color: var(--sg-accent, #FF2ED1);
      text-shadow: 0 0 28px rgba(255, 46, 209, 0.6);
    }
    #${rootId} .cm-round-board {
      display: grid;
      gap: 12px;
      width: 100%;
      height: 100%;
      place-content: center;
      perspective: 1200px;
    }
    #${rootId} .cm-tile {
      /* Square cells driven by --cm-tile-size (computed in round.ts from
         min(boardW/cols, boardH/rows) and applied to every grid cell via
         grid-template-columns/rows). aspect-ratio: 1 is a belt-and-braces
         guard for browsers that don't honor the explicit row sizing. */
      aspect-ratio: 1;
      width: var(--cm-tile-size, 100px);
      height: var(--cm-tile-size, 100px);
      min-width: 0;
      min-height: 0;
      position: relative;
      /* CRITICAL: preserve-3d so the shake animation's transform on this
         element doesn't flatten the .cm-tile-inner's rotateY(180deg) flip.
         Without this, applying ANY transform to .cm-tile (including the
         shake's translateX(0) at keyframes 0%/100%) forces children to be
         rendered to a 2D plane before the parent transform is applied.
         The visual effect was: mismatch path showed the shake but the
         3D flip rotation snapped flat for the entire 200ms shake window,
         making it look like the card never flipped up. preserve-3d makes
         the tile a "passing-through" element in the 3D rendering context
         established by the .cm-round-board ancestor's perspective, so
         children's 3D transforms still render correctly during the shake. */
      transform-style: preserve-3d;
    }
    #${rootId} .cm-tile-inner {
      position: relative;
      width: 100%;
      height: 100%;
      transform-style: preserve-3d;
      /* Force a dedicated compositor layer for the flip. Without this,
         OBS Browser Source / CEF can defer creating the 3D rendering
         context until the first transform change, by which point the
         shake animation on the parent .cm-tile is already running and
         the 3D context is created "too late" — viewers see only the
         shake, not the flip. will-change pre-allocates the layer so the
         flip's rotateY transition runs on the GPU from the first frame. */
      will-change: transform;
      /* Flip duration tuned to 400ms per design spec — slow enough that a
         viewer's eye registers the rotation, fast enough that a mismatch
         (flip-up + peek + flip-back) fits inside the ~1.5s peek budget. */
      transition: transform 400ms cubic-bezier(.4, .0, .2, 1);
    }
    #${rootId} .cm-tile-flipped .cm-tile-inner {
      transform: rotateY(180deg);
    }
    #${rootId} .cm-tile-face {
      position: absolute;
      inset: 0;
      /* Both webkit-prefixed + standard. Safari + older mobile WebKit
         still need the prefix to honour backface culling reliably. */
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
      overflow: hidden;
    }

    /* ---- Card back: number-only over a brand gradient (Bug 1 fix) ----

       NOTE: do NOT set position: relative here — it would override the
       position: absolute from .cm-tile-face above, causing the back to
       collapse to its content height (the number) so face-down tiles
       rendered as wide pill-shapes (~2:1) instead of squares matching the
       face-up tile. The ::after inner-border below is positioned relative
       to the absolute .cm-tile-face just fine. */
    #${rootId} .cm-tile-back {
      background:
        radial-gradient(circle at 30% 25%, rgba(255, 255, 255, 0.18) 0%, transparent 55%),
        linear-gradient(135deg,
          var(--sg-primary, #8A2BE2) 0%,
          #5B1AA8 60%,
          var(--sg-secondary, #00E5FF) 100%);
      border: 2px solid rgba(255, 255, 255, 0.20);
    }
    #${rootId} .cm-tile-back::after {
      /* Subtle inner border accent for depth. */
      content: '';
      position: absolute;
      inset: 6px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      border-radius: 10px;
      pointer-events: none;
    }
    #${rootId} .cm-tile-back-num {
      position: relative;
      z-index: 2;
      font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
      font-weight: 700;
      /* Container query keeps the number readable across grid sizes. cqi =
         1% of the inline-size of the nearest container. We size the number
         relative to the tile so 6x8 (smallest, ~150px tile) and 4x4 still
         look right. */
      font-size: clamp(36px, 52cqi, 96px);
      line-height: 1;
      color: #FFFFFF;
      letter-spacing: 0.02em;
      text-shadow:
        0 2px 0 rgba(0, 0, 0, 0.35),
        0 0 18px rgba(255, 255, 255, 0.45),
        0 0 4px rgba(0, 0, 0, 0.6);
      font-variant-numeric: tabular-nums;
    }
    #${rootId} .cm-tile { container-type: inline-size; }

    #${rootId} .cm-tile-front {
      transform: rotateY(180deg);
      background: var(--sg-tile-bg, #1A1A24);
      border: 2px solid var(--sg-secondary, #00E5FF);
    }
    #${rootId} .cm-tile-symbol-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      pointer-events: none;
    }
    #${rootId} .cm-tile-symbol-img[hidden] { display: none; }
    #${rootId} .cm-tile-symbol {
      font-size: 64px;
      line-height: 1;
    }
    /* Match visual: simple static solid green border on the front face. No
       glow, no pulse, no keyframe — bare-minimum "you got it" indicator. */
    #${rootId} .cm-tile-matched .cm-tile-front {
      background: rgba(34, 197, 94, 0.18);
      border: 4px solid #00C853;
    }
    #${rootId} .cm-round-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 24px;
      font-family: 'Inter', system-ui, sans-serif;
      font-size: 22px;
      color: rgba(226, 232, 240, 0.80);
      letter-spacing: 0.04em;
      /* Fixed band height matches the 76px reserve in round.ts so the board
         row never reflows from footer line-height shifts. */
      height: 76px;
      box-sizing: border-box;
      overflow: hidden;
    }
    #${rootId} .cm-round-help {
      font-family: 'Oswald', 'Bebas Neue', Impact, sans-serif;
      font-size: 26px;
      letter-spacing: 0.22em;
      /* White help text — purple glow was tinting the letters visibly,
         so swap to the same cyan-soft glow the round header uses for
         visual parity with the white HUD title row. */
      color: #FFFFFF;
      text-shadow: 0 0 20px rgba(0, 229, 255, 0.35), 0 2px 4px rgba(0, 0, 0, 0.55);
      text-transform: uppercase;
      display: inline-flex;
      align-items: center;
      gap: 14px;
    }
    /* Example chip — bordered, monospace, WHITE so it reads cleanly against
       the dark animated stage. Keeps the cyan border + halo for brand
       continuity. Previously used cyan text which read as purple-ish on the
       gradient bg per user feedback. Mirrors the lobby <code> chip styling. */
    #${rootId} .cm-round-help-example {
      font-family: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace;
      font-size: 22px;
      letter-spacing: 0.10em;
      color: #FFFFFF;
      background: rgba(0, 229, 255, 0.10);
      border: 1px solid rgba(0, 229, 255, 0.45);
      border-radius: 8px;
      padding: 4px 10px;
      text-transform: none;
      text-shadow: 0 0 10px rgba(0, 229, 255, 0.45);
    }
    /* Chat-guess bubble in the footer — plain text on a translucent
       background. No entry/exit animation; the colour change between match
       and miss is the only visual feedback. */
    #${rootId} .cm-round-flip {
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      font-size: 26px;
      letter-spacing: 0.06em;
      padding: 6px 16px;
      border-radius: 999px;
      background: rgba(15, 23, 42, 0.55);
      border: 1px solid rgba(255, 255, 255, 0.18);
      color: rgba(226, 232, 240, 0.95);
      max-width: 720px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    #${rootId} .cm-round-flip:empty {
      visibility: hidden;
    }
    #${rootId} .cm-round-flip.match {
      color: #22C55E;
      border-color: rgba(34, 197, 94, 0.55);
    }
    #${rootId} .cm-round-flip.miss {
      color: #FCA5A5;
      border-color: rgba(239, 68, 68, 0.55);
    }
  `;
  document.head.appendChild(s);
}
